<!-- Cachebuster: 27 (used to clear cache on bit.dev, update if builds are not updated) -->
<template>
  <div>
    <transition name="pending-bonuses-wrapper-transition">
      <div v-show="showPendingBonusesV2" :class="{'pendingBonusActive': selectedBonus}" class="pending-bonuses-wrapper">
        <div class="pending-bonuses">
          <div class="close-btn" @click="closeButtonClicked"></div>
          <div v-if="showNavBar" class="pending-bonuses-navigation-bar">
            <div class="logo" v-if="!selectedBonus">
              <img v-if="navBarLogo" :src="navBarLogo">
            </div>
            <div class="back-button" v-if="selectedBonus" @click="backToList">{{translations.pendingBonusesBackButtonText}}</div>
            <div class="navbar-titles">
              <h1 v-if="!selectedBonus" class="title">{{translations.pendingBonusesTitle}}</h1>
              <h2 v-if="!selectedBonus && translations.pendingBonusesSubTitle" class="sub-title">{{translations.pendingBonusesSubTitle}}</h2>
            </div>
            <div class="contact-details" :class="{'hidden' : !selectedBonus}">
              {{translations.questionsTop}}
              <br>
              <a :href="mailtoLink">{{contactEmail}}</a>
            </div>
          </div>
          <div class="no-bonuses" v-if="!selectedBonus && bonuses.length == 0 && !isLoading">
            <span>{{translations.pendingBonusesEmpty}}</span>
          </div>
          <div v-if="isLoading" class="spinner-wrapper">
            <Spinner></Spinner>
          </div>
          <div v-show="!selectedBonus" v-if="bonuses.length > 0 && !isLoading" class="bonuses-list-wrapper">
            <div class="bonuses-list">
              <Listitem v-for="pendingBonus in bonuses" :pendingBonus="pendingBonus" :locale="locale" :expiryDateString="expiryString(pendingBonus.ExpiryDate)" :translations="translations" :key="pendingBonus.Id" @clicked-bonus="showBonus"></Listitem>
            </div>
          </div>
          <PendingBonus v-if="selectedBonus" :authToken="authToken" :fusionUrl="fusionUrl" :depositPageUrl="depositPageUrl" :expiryDateString="expiryString(selectedBonus.ExpiryDate)" :prismaUrl="prismaUrl" :pendingBonus="selectedBonus" :translations="translations" :mailtoLink="mailtoLink" :showNavBar="showNavBar" :termsAboveButtons="termsAboveButtons" :showTermsHeader="showTermsHeader"></PendingBonus>
        </div>
        <div @click="fogClicked" class="overlay-fog">
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { differenceInSeconds, format } from 'date-fns';

import Listitem from './Listitem.vue';
import PendingBonus from './PendingBonus.vue';
import Spinner from '../Spinner/Spinner.vue';

import { getPendingBonuses } from '../../api/rewards';
import { logWarn } from '../../utils/log';
import { convertToLocal } from '../../utils/date';

export default {
  name: 'PendingBonusesV2',
  components: {
    Spinner,
    Listitem,
    PendingBonus,
  },
  props: {
    authToken: {
      type: String,
    },
    fusionUrl: {
      type: String,
    },
    prismaUrl: {
      type: String,
    },
    userId: {
      type: Number,
    },
    brandId: {
      type: [String, Number],
    },
    brandName: {
      type: String,
    },
    showPendingBonusesV2: {
      type: Boolean,
    },
    locale: {
      type: Object,
    },
    translations: {
      type: Object,
    },
    navBarLogo: {
      type: String,
    },
    contactEmail: {
      type: String,
    },
    termsAboveButtons: {
      type: Boolean,
    },
    closeModalOnFogClick: {
      type: Boolean,
      default: false,
    },
    depositPageUrl: {
      type: String,
    },
    selectedPendingBonusId: {
      type: Number,
      default: -1,
    },
    showTermsHeader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pendingBonuses: [],
      localPendingBonuses: [],
      isLoading: true,
      initialLoadDone: false,
      selectedBonus: null,
      showNavBar: true,
      unreadNotificationsCount: 0,
      shouldAnimateTransitions: true,
    };
  },
  mounted() {
    window.addEventListener('send-local-pending-bonus', (payload) => {
      this.addLocalPendingBonus(payload.detail);
    });

    if (this.authToken && this.authToken !== '') {
      this.initPendingBonusesV2();
    }

    window.addEventListener('set-selected-pending-bonus', (payload) => {
      this.showBonus(payload.detail.bonusId);
    });

    if (typeof window.matchMedia !== 'undefined') {
      const matchMedia = window.matchMedia('(max-width: 767px)');
      matchMedia.addListener((event) => {
        this.$store.dispatch('setMobileResponsive', event.matches);
      });
      this.$store.dispatch('setMobileResponsive', matchMedia.matches);
    }
  },
  methods: {
    addLocalPendingBonus(payload) {
      this.localPendingBonuses.unshift(payload);
      this.$emit('badgeChanged', this.bonuses.length);
    },
    initPendingBonusesV2() {
      this.loadPendingBonuses();
    },
    loadPendingBonuses() {
      this.isLoading = true;

      getPendingBonuses(this.authToken, this.prismaUrl, this.fusionUrl).then((response) => {
        if (!response.data.Success) {
          const errArr = ['Could not get pending bonuses'];
          if (response.data.Errors.length > 0) {
            if (response.data.Errors && response.data.Errors.length > 0) {
              response.data.Errors.forEach((error, index) => {
                errArr.push(`Error ${index}: ${error.Message}`);
              });
            }
          }
          logWarn(errArr);
          return;
        }

        this.pendingBonuses = response.data.Data || [];
        this.pendingBonuses.sort((a, b) => {
          const dateA = `${a.DateCreated.replace(' ', 'T')}Z`;
          const dateB = `${b.DateCreated.replace(' ', 'T')}Z`;
          return new Date(dateB) - new Date(dateA);
        });
        this.$emit('badgeChanged', this.bonuses.length);
      }).catch((error) => {
        logWarn(error);
      }).finally(() => {
        this.isLoading = false;
        this.initialLoadDone = true;
      });
    },
    backToList() {
      this.selectedBonus = null;
    },
    closeButtonClicked() {
      this.$emit('togglePendingBonusesV2');
      setTimeout(() => {
        this.selectedBonus = null;
        this.showNavBar = true;
      }, 500);
    },
    showBonus(id) {
      this.selectedBonus = this.bonuses.find(
        obj => obj.Id === id,
      );
    },
    fogClicked(e) {
      if (e.target.classList.contains('overlay-fog') && this.closeModalOnFogClick) {
        this.$emit('togglePendingBonusesV2');
        this.selectedBonus = null;
      }
    },
    expiryString(expiryDate) {
      if (!expiryDate || expiryDate === '1970-01-01 00:00:00') {
        return '';
      }

      const date = convertToLocal(expiryDate);
      const s = differenceInSeconds(date, new Date()); // = Seconds Left

      const minute = 60;
      const hour = minute * 60;
      const day = hour * 24;

      if (s > (day * 30)) return `${this.translations.pendingBonusesExpires} ${format(date, 'yyyy-MM-dd')}`;
      if (s > (hour * 72)) return `${this.translations.pendingBonusesExpiresIn} ${Math.floor(s / day)}${this.translations.days}`;
      if (s > hour) return `${this.translations.pendingBonusesExpiresIn} ${Math.floor(s / hour)}${this.translations.hours}`;
      if (s > minute) return `${this.translations.pendingBonusesExpiresIn} ${Math.floor(s / minute)}${this.translations.minutes}`;
      return `${this.translations.pendingBonusesExpiresIn} ${s}s`;
    },
  },
  computed: {
    mailtoLink() {
      return `mailto:${this.contactEmail}`;
    },
    bonuses() {
      const bonuses = [...this.pendingBonuses, ...this.localPendingBonuses];

      bonuses.concat().sort((a, b) => {
        const dateA = `${a.DateCreated.replace(' ', 'T')}Z`;
        const dateB = `${b.DateCreated.replace(' ', 'T')}Z`;
        return new Date(dateB) - new Date(dateA);
      });
      return bonuses;
    },
  },
  watch: {
    authToken() {
      if (this.authToken !== '' && this.authToken !== null && !this.initialLoadDone) {
        this.initPendingBonusesV2();
      }
    },
    showPendingBonusesV2(show) {
      if (show) {
        if (this.authToken && this.authToken !== '') {
          this.initPendingBonusesV2();
        }
      }
    },
  },
};
</script>

<style lang="less">
@import (css) url("https://fonts.googleapis.com/css?family=Lato:400,700,900");

@maxWidth: 600px;
@navBarHeight: 55px;
@borderRadius: 6px;

#fasttrack-crm {
  .overlay-fog {
    background-color: rgba(0, 0, 0, 0.8);
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1000;
  }

  .hidden {
    opacity: 0;
  }

  .pending-bonuses-wrapper {
    font-family: "Lato", sans-serif;
    background-color: rgba(0, 0, 0, 0.1);
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 999999; /* Trying to override all chat buttons and similiar */
    min-width: 100vw;
    min-height: 85vh;

    * {
      box-sizing: border-box;
      -webkit-font-smoothing: antialiased;
    }
  }

  .pending-bonuses-wrapper-transition-enter-active,
  .pending-bonuses-wrapper-transition-leave-active {
    transition: opacity 0.2s;
  }
  .pending-bonuses-wrapper-transition-enter,
  .pending-bonuses-wrapper-transition-leave-to {
    opacity: 0;
  }

  .pending-bonuses {
    width: 95%;
    max-width: @maxWidth;
    min-height: 200px;
    max-height: 96%;
    background-color: white;
    border-radius: @borderRadius;
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.3);
    z-index: 5000;
    position: relative;
    display: flex;
    flex-direction: column;
    margin-bottom: 50px;
    margin-top: 10px;

    @media all and (max-width: 768px) {
      height: 100vh;
      margin-bottom: 0px;
    }
  }

  .pending-bonuses-navigation-bar {
    min-height: @navBarHeight;
    padding: 10px 17px 10px 17px;
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: @borderRadius @borderRadius 0px 0px;
    box-shadow: inset 0 -1px 0 0 rgba(0, 0, 0, 0.1);
    z-index: 1;

    .logo {
      width: 100px;
      height: 100%;
      max-height: 50px;
      display: flex;
      align-items: center;

      img {
        max-height: 50px;
        height: auto;
        max-width: 100%;
        width: auto;
        display: inline-block;
      }
    }

    h1.title {
      margin: 0;
      font-family: "Lato", sans-serif;
      padding: 0;
      font-size: 18px;
      font-weight: 900;
      color: #333;
      text-align: center;
    }

    h2.sub-title {
      margin: 0;
      margin-top: 5px;
      font-family: "Lato", sans-serif;
      padding: 0;
      font-size: 12px;
      font-weight: 600;
      color: #333;
      text-align: center;
    }

    .contact-details {
      font-size: 8px;
      line-height: 1;
      color: #000;
      text-align: right;
      width: 100px;

      a {
        text-decoration: none;
        color: inherit;
      }
    }
  }

  .spinner-wrapper {
    width: 100%;
    height: 100%;
    max-height: ~"calc(100% - @{navBarHeight})";
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }

  .no-bonuses {
    height: 100%;
    max-height: ~"calc(100% - @{navBarHeight})";

    fill: rgba(0, 0, 0, 0.3);

    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin-top: 70px;
    margin-bottom: 70px;

    span {
      font-size: 20px;
      line-height: 1.5;
      color: rgba(0, 0, 0, 0.3);
      max-width: 200px;
      text-align: center;
    }

    svg {
      width: 75px;
      height: auto;
      margin-bottom: 20px;

      fill: inherit;
      g {
        fill: inherit;
        path {
          fill: inherit;
        }
      }
    }
  }

  .close-btn {
    font-family: "Lato", sans-serif;
    position: absolute;
    top: 0;
    right: 0;
    margin-right: -7px;
    margin-top: -7px;
    cursor: pointer;
    background-color: #fff;
    border-radius: 100%;
    width: 32px;
    height: 32px;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
    transition: all 0.1s ease-in-out;
    z-index: 500;

    &:before,
    &:after {
      content: "";
      position: absolute;
      top: 9px;
      left: 15px;
      width: 2px;
      height: 15px;
      border-radius: 2px;
      background-color: #333;
      transform: rotate(45deg);
    }

    &:after {
      transform: rotate(-45deg);
    }

    &:hover {
      transform: scale(1.1);
    }
  }

  .back-button {
    cursor: pointer;
    border-radius: 2px;
    height: 24px;
    padding: 0px 10px;
    position: relative;
    padding-left: 18px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    line-height: 1;
    font-weight: 900;
    color: #000;

    &:before,
    &:after {
      content: "";
      position: absolute;
      background-color: #000;
      left: 8px;
      top: 50%;
      margin-top: -6px;
      width: 2px;
      height: 7px;
      border-radius: 1px;
      -webkit-transform: rotate(30deg);
      transform: rotate(30deg);
    }

    &:after {
      margin-top: -1px;
      -webkit-transform: rotate(-30deg);
      transform: rotate(-30deg);
    }
  }

  .bonuses-list-wrapper {
    width: 100%;
    height: 100%;
    min-height: 60vh;
    overflow: hidden;
    border-radius: 0px 0px @borderRadius @borderRadius;
    max-height: ~"calc(100% - @{navBarHeight})";
    position: relative;
  }

  .bonuses-list {
    position: absolute;
    top: 0;
    padding: 30px;
    margin: 0;
    width: 100%;
    max-width: @maxWidth;
    height: 100%;
    list-style-type: none;
    overflow-x: hidden;
    overflow-y: scroll;
    text-align: left;
  }
}
</style>
